import { useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router'
import { Tabs, ChecklistsContainer } from 'components/ideal-core-ui'
import { URLS } from 'utils/constants'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { LeadDetails } from './LeadDetails'
import { LeadNotes } from './LeadNotes'
import { LeadSpend } from './LeadSpend'
import { FaTimes, FaAngleDoubleRight, FaArchive, FaTrash, FaInfoCircle } from 'react-icons/fa'
import s from './Lead.scss'
import {
  makeLeadsGetRequest,
  makeLeadsPutRequest,
  makeLeadsPatchRequest,
  makeLeadsDeleteRequest
} from 'utils/api'
import { utcCurrentDate } from 'utils/helpers'
import { useImmer } from 'use-immer'
import { toLead } from '../serializers'
import { Button, UncontrolledTooltip } from 'reactstrap'
import { CreateContactModal } from './CreateContactModal'
import { CreateCompanyModal } from './CreateCompanyModal'
import { CreateDealModal } from './CreateDealModal'
import { useDispatch } from 'react-redux'
import { toDeal } from 'components/Deals/serializers.js'
import { MdUnarchive } from 'react-icons/md'
import swal from 'sweetalert'
import { EmailContactModalContainer } from 'components/common/modals/EmailContactModalContainer'
import { toEmailContact } from './serializers'
import cn from 'classnames'

const Lead = () => {
  let { id } = useParams()
  const location = useLocation()
  const activeTab = location.pathname.split('/')[3]
  const navigate = useNavigate()
  const [lead, setLead] = useImmer({ name: '', followupDate: utcCurrentDate(), tags: [] })
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false)
  const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] = useState(false)
  const [isCreateDealModalOpen, setIsCreateDealModalOpen] = useState(false)
  const [showEmailContactModal, setShowEmailContactModal] = useState(false)

  const dispatch = useDispatch()

  useEffect(() => {
    ;(async () => {
      const response = await makeLeadsGetRequest(`/v1/leads/${id}`)
      setLead(toLead(response))
    })()
  }, [id, setLead])

  const closeLeadDetails = () => {
    navigate(URLS.AUTH_LEADS, { state: { fromLead: true } })
  }

  const createContact = async (contact, copy) => {
    const { shouldCopyNotes, shouldCopyChecklist, shouldCopySpend } = copy
    await makeLeadsPutRequest(
      `/v1/leads/convert/${id}/contact?shouldCopyChecklist=${shouldCopyChecklist}&shouldCopyNotes=${shouldCopyNotes}&shouldCopySpend=${shouldCopySpend}`,
      contact
    )
    setIsCreateContactModalOpen(false)
    dispatch({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Contact created successfuly',
        level: 'success'
      }
    })
  }

  const createCompany = async (company, copy) => {
    const { shouldCopyNotes, shouldCopyChecklist, shouldCopySpend } = copy
    await makeLeadsPutRequest(
      `/v1/leads/convert/${id}/company?shouldCopyChecklist=${shouldCopyChecklist}&shouldCopyNotes=${shouldCopyNotes}&shouldCopySpend=${shouldCopySpend}`,
      company
    )
    setIsCreateCompanyModalOpen(false)
    dispatch({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Company created successfuly',
        level: 'success'
      }
    })
  }

  const createDeal = async (payload, cb, copy) => {
    const { shouldCopyNotes, shouldCopyChecklist } = copy
    const { deal, internalTeam } = payload
    const response = await makeLeadsPutRequest(
      `/v1/leads/convert/${id}/deal?shouldCopyChecklist=${shouldCopyChecklist}&shouldCopyNotes=${shouldCopyNotes}`,
      { ...toDeal(deal), internalTeam }
    )
    cb?.(response.id)
    setIsCreateDealModalOpen(false)
    dispatch({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Opportunity created successfuly',
        level: 'success'
      }
    })
  }

  const toggleArchiveLead = async () => {
    await makeLeadsPatchRequest(`/v1/leads/${id}`, {
      isArchived: !lead.isArchived
    })
    setLead(draft => {
      draft.isArchived = !draft.isArchived
    })
  }

  const deleteLead = async () => {
    const willRemove = await swal({
      title: 'Are you sure you want to delete this lead?',
      text: 'This action can’t be undone',
      buttons: ['Cancel', 'Yes'],
      icon: 'warning'
    })

    if (willRemove) {
      await makeLeadsDeleteRequest(`/v1/leads/${id}`)
      closeLeadDetails()
    }
  }

  const toggleEmailContactModal = () => setShowEmailContactModal(prev => !prev)

  return (
    <>
      <div className={s.container}>
        <div style={{ padding: '30px 50px' }}>
          <h3>{lead.name}</h3>
          {lead.breadCrumb && (
            <p style={{ fontSize: 14, color: '#007bff' }}>
              {lead.breadCrumb}
              <FaInfoCircle id="Type1" style={{ marginLeft: 5 }} />
              <UncontrolledTooltip placement="top" target="Type1">
                This is the email address of your lead. Emails sent to this email address will be
                saved under the lead notes
              </UncontrolledTooltip>
            </p>
          )}
          <FaTimes onClick={closeLeadDetails} className={s.closeBtn} />
          <FaAngleDoubleRight onClick={closeLeadDetails} className={s.closeArrows} />
          {lead.email && (
            <Button className={cn(s.emailBtn, s.createBtn)} onClick={toggleEmailContactModal}>
              Email Lead
            </Button>
          )}
          <Button
            color="primary"
            className={s.createBtn}
            onClick={() => setIsCreateContactModalOpen(true)}
          >
            Create Contact from Lead
          </Button>
          <Button
            color="primary"
            className={s.createBtn}
            onClick={() => setIsCreateCompanyModalOpen(true)}
          >
            Create Company from Lead
          </Button>
          <Button
            color="primary"
            className={s.createBtn}
            onClick={() => setIsCreateDealModalOpen(true)}
          >
            Create Opportunity from Lead
          </Button>
          <div className={s.actionsWrapper}>
            {lead.isArchived ? (
              <MdUnarchive
                style={{ fontSize: 20 }}
                onClick={toggleArchiveLead}
                className={s.action}
              />
            ) : (
              <FaArchive onClick={toggleArchiveLead} title="Archive" className={s.action} />
            )}
            <FaTrash onClick={deleteLead} title="Delete" className={s.action} />
          </div>
        </div>
        <Tabs
          className={s.tabs}
          contentClassName={s.tabContent}
          withRoutes
          activeTabKey={activeTab || 'details'}
          tabs={[
            {
              index: true,
              key: 'details',
              label: 'Details',
              path: URLS.AUTH_LEAD_DETAILS,
              element: <LeadDetails lead={lead} setLead={setLead} leadId={id} />
            },
            {
              key: 'notes',
              label: 'Notes',
              path: URLS.AUTH_LEAD_NOTES,
              element: <LeadNotes leadId={id} />
            },
            {
              label: 'Checklists',
              key: 'checklists',
              path: URLS.AUTH_LEAD_CHECKLISTS,
              element: <ChecklistsContainer entity="lead" module="leads" />
            },
            {
              label: 'Spend',
              key: 'spend',
              path: URLS.AUTH_LEAD_SPEND,
              element: <LeadSpend leadId={id} />
            }
          ]}
        />
      </div>
      {isCreateContactModalOpen && (
        <CreateContactModal
          isCreateContactModalOpen={isCreateContactModalOpen}
          toggleCreateContactModal={() => setIsCreateContactModalOpen(!isCreateContactModalOpen)}
          lead={lead}
          createContact={createContact}
        />
      )}
      {isCreateCompanyModalOpen && (
        <CreateCompanyModal
          isCreateCompanyModalOpen={isCreateCompanyModalOpen}
          toggleCreateCompanyModal={() => setIsCreateCompanyModalOpen(!isCreateCompanyModalOpen)}
          lead={lead}
          createCompany={createCompany}
        />
      )}
      {isCreateDealModalOpen && (
        <CreateDealModal
          isCreateDealModalOpen={isCreateDealModalOpen}
          toggleCreateDealModal={() => setIsCreateDealModalOpen(!isCreateDealModalOpen)}
          lead={lead}
          createDeal={createDeal}
        />
      )}
      {showEmailContactModal && (
        <EmailContactModalContainer
          toggleEmailContactModal={toggleEmailContactModal}
          showEmailContactModal={showEmailContactModal}
          contact={[toEmailContact(lead)]}
          app="leads"
          leadId={lead.id}
        />
      )}
    </>
  )
}

export default Lead
